import { useState, useEffect, useRef } from 'react';
import {
    Box,
    Button,
    Grid,
    InputLabel,
    OutlinedInput,
    TextField,
    Typography,
    Divider,
    Switch,
    FormControl,
    Select,
    MenuItem
} from '@mui/material';
import { toast } from 'react-toastify';
import { gridSpacing } from 'utils/constants';
import MainCard from 'components/Cards/MainCard';
import SubCard from 'components/Cards/SubCard';
import { validateNumber } from 'utils/commonHelpers';
import ApiHelper from 'utils/apiHelper';

const SecuritySettings = () => {
    const [securitySettingsData, setSecuritySettingsData] = useState({
        passwordSettings: {
            active2Factor: false,
            sso: false,
            wakeportal: false,
            attemptsLimit: 0,
            expirationLimit: 0,
            id: 0,
            pwdLength: 0,
            requireCapitalLetter: false,
            requireLowercase: false,
            requireNum: false,
            requireSpecialCharacter: false,
            reuseCycle: 0,
            specialCharacters: ''
        },
        ssoSettings: {
            idpType: '',
            idpUrl: '',
            idpEntityId: '',
            clientId: '',
            idpCert: ''
        },
        autoUpgrade: false,
        current_versions: {
            windows: '',
            macOS: ''
        },
        selected_versions: {
            windows: '',
            macOS: ''
        },
        available_versions: {
            windows: [],
            macOS: []
        },
        domain: '',
        onprem: false,
        userInfo: {
            root_admin: false,
            server_admin: false,
            org_admin: false,
            group_admin: false,
            user_admin: false
        }
    });
    // Global reCAPTCHA site key (server-wide). canEdit is true only for Root Admins.
    const [recaptcha, setRecaptcha] = useState({ siteKey: '', canEdit: false });
    // Result of the SSO "Test Connection" probe: { ok, checks: [{name, status, detail}] }.
    const [ssoTest, setSsoTest] = useState({ running: false, result: null });
    useEffect(() => {
        getSecuritySettingsData();
        getRecaptchaKey();
    }, []);

    const getSecuritySettingsData = () => {
        ApiHelper.get('/api/settings/security_settings/')
            .then((res) => {
                setSecuritySettingsData(res.data);
            })
            .catch((err) => {
                if (err.response.status !== 401) {
                    toast.error(err.response.data.message);
                }
            });
    };

    const getRecaptchaKey = () => {
        ApiHelper.get('/api/settings/recaptcha_key/')
            .then((res) => {
                setRecaptcha({ siteKey: res.data.siteKey || '', canEdit: !!res.data.canEdit });
            })
            .catch(() => {
                /* non-fatal: the field just won't be shown */
            });
    };

    const handleSaveRecaptchaKey = () => {
        ApiHelper.put('/api/settings/recaptcha_key/', { siteKey: recaptcha.siteKey })
            .then((res) => {
                setRecaptcha((prev) => ({ ...prev, siteKey: (res.data && res.data.siteKey) || '' }));
                toast.success('reCAPTCHA site key updated');
            })
            .catch(() => {
                toast.error('Failed to update the reCAPTCHA site key');
            });
    };

    const handleChangePasswordSettingsData = (e) => {
        if (e.target.value === '') toast.error('Please type the value');
        let type;
        if (
            e.target.name === 'pwdLength' ||
            e.target.name === 'attemptsLimit' ||
            e.target.name === 'expirationLimit' ||
            e.target.name === 'reuseCycle'
        ) {
            type = 'integer';
        } else {
            type = 'string';
        }

        if (type == 'string' && e.target.value.length > securitySettingsData.limit_fields[e.target.name]) {
            toast.warning('This field must be ' + securitySettingsData.limit_fields[e.target.name] + ' characters or less.');
        } else {
            if (validateNumber(e.target.value, type) === true) {
                setSecuritySettingsData({
                    ...securitySettingsData,
                    [e.target.id]: e.target.value
                });
            } else {
                if (type === 'integer') toast.error('It should be an integer value.');
                else toast.error('It should be a number');
            }
        }
    };

    const handleChangeSelectedVersion = (e) => {
        setSecuritySettingsData({
            ...securitySettingsData,
            selected_versions: {
                ...securitySettingsData['selected_versions'],
                [e.target.name]: e.target.value
            }
        });
    };

    // Hidden file inputs triggered by the per-platform "Upload Installer" buttons
    // in the Device Upgrade Settings card.
    const windowsInstallerInput = useRef(null);
    const macInstallerInput = useRef(null);

    // Validate the selected files, then POST them to the server, which parses the
    // version from the filename, saves the file(s) to the org's installers folder,
    // and creates/updates the raptor_client_versions row (selected=False).
    const handleInstallerUpload = (clienttype, fileList) => {
        const files = Array.from(fileList || []);
        const extOf = (name) => name.slice(name.lastIndexOf('.')).toLowerCase();
        const exts = files.map((f) => extOf(f.name));

        if (clienttype === 'Windows') {
            if (!(files.length === 2 && exts.includes('.exe') && exts.includes('.msi'))) {
                toast.error('Please select both the .exe and the .msi Windows installers together.');
                return;
            }
        } else if (clienttype === 'MacOS') {
            if (!(files.length === 1 && exts[0] === '.pkg')) {
                toast.error('Please select a single .pkg MacOS installer.');
                return;
            }
        }

        const formData = new FormData();
        formData.append('clienttype', clienttype);
        files.forEach((f) => formData.append('files', f));

        ApiHelper.post('/api/settings/upload_installer/', formData)
            .then((res) => {
                const version = res.data && res.data.version;
                toast.success(version ? `Installer uploaded (v${version})` : 'Installer uploaded');
                getSecuritySettingsData();
            })
            .catch((err) => {
                toast.error((err.response && err.response.data && err.response.data.message) || 'Failed to upload the installer');
            });
    };

    const handleChangeSwitchData = (e) => {
        setSecuritySettingsData({
            ...securitySettingsData,
            [e.target.id]: {
                ...securitySettingsData[e.target.id],
                [e.target.name]: e.target.checked
            }
        });
    };

    const handleChangeSsoSettings = (e) => {
        setSecuritySettingsData({
            ...securitySettingsData,
            ssoSettings: {
                ...(securitySettingsData.ssoSettings || {}),
                [e.target.name]: e.target.value
            }
        });
    };

    const handleTestSsoConnection = () => {
        const sso = securitySettingsData.ssoSettings || {};
        setSsoTest({ running: true, result: null });
        // Send the current (possibly unsaved) form values so the admin can test
        // before saving. The backend never persists what it receives here.
        ApiHelper.post('/api/settings/sso_test/', {
            sso: securitySettingsData.passwordSettings.sso,
            sso_idp_type: sso.idpType || '',
            sso_idp_url: sso.idpUrl || '',
            sso_idp_entity_id: sso.idpEntityId || '',
            sso_client_id: sso.clientId || '',
            sso_idp_cert: sso.idpCert || ''
        })
            .then((res) => {
                setSsoTest({ running: false, result: res.data });
                if (res.data.ok) {
                    toast.success('SSO configuration looks good');
                } else {
                    toast.error('SSO test found problems — see the details below');
                }
            })
            .catch(() => {
                setSsoTest({ running: false, result: null });
                toast.error('Failed to run the SSO connection test');
            });
    };

    const handleSubmit = () => {
        if (
            securitySettingsData.passwordSettings.pwdLength === '' ||
            securitySettingsData.passwordSettings.attemptsLimit === '' ||
            securitySettingsData.passwordSettings.expirationLimit === '' ||
            securitySettingsData.passwordSettings.reuseCycle === ''
        ) {
            toast.error('There are some empty values');
            return true;
        }
        ApiHelper.put(`/api/settings/security_settings/${securitySettingsData.passwordSettings.id}/`, {
            pwd_length: securitySettingsData.passwordSettings.pwdLength,
            pwd_lockout: securitySettingsData.passwordSettings.attemptsLimit,
            pwd_expire: securitySettingsData.passwordSettings.expirationLimit,
            pwd_history: securitySettingsData.passwordSettings.reuseCycle,
            active_two_factor: securitySettingsData.passwordSettings.active2Factor,
            sso: securitySettingsData.passwordSettings.sso,
            wakeportal: securitySettingsData.passwordSettings.wakeportal,
            pwd_cap_req: securitySettingsData.passwordSettings.requireCapitalLetter,
            pwd_lwr_req: securitySettingsData.passwordSettings.requireLowercase,
            pwd_num_req: securitySettingsData.passwordSettings.requireNum,
            pwd_spec_req: securitySettingsData.passwordSettings.requireSpecialCharacter,
            spec_char: securitySettingsData.passwordSettings.specialCharacters,
            auto_upgrade: securitySettingsData.autoUpgrade,
            sso_idp_type: (securitySettingsData.ssoSettings || {}).idpType || '',
            sso_idp_url: (securitySettingsData.ssoSettings || {}).idpUrl || '',
            sso_idp_entity_id: (securitySettingsData.ssoSettings || {}).idpEntityId || '',
            sso_client_id: (securitySettingsData.ssoSettings || {}).clientId || '',
            sso_idp_cert: (securitySettingsData.ssoSettings || {}).idpCert || '',
            selected_version_windows: securitySettingsData.selected_versions.windows,
            selected_version_macOS: securitySettingsData.selected_versions.macOS,
            domain: securitySettingsData.domain
        })
            .then(() => {
                toast.success('Successfully updated');
            })
            .catch(() => {
                toast.error('Failed to update the security settings data');
            });
    };

    if (!securitySettingsData) {
        return null;
    } else {
        return (
            <MainCard title="Security Settings" cardHeaderTitle="System Options" cardHeaderShow={true}>
                <Grid container spacing={gridSpacing}>
                    <Grid item xs={12} sx={{ position: 'relative', mt: 2, mb: 1 }}>
                        <Box sx={{ position: 'absolute', right: '0', top: '0' }}>
                            <Button size="large" color="secondary" variant="contained" onClick={handleSubmit}>
                                Save
                            </Button>
                            <Button onClick={getSecuritySettingsData}>Cancel</Button>
                        </Box>
                    </Grid>
                    <Grid item xs={6}>
                        <SubCard title="Password Complexity Settings">
                            <Grid container>
                                <Grid item xs={4} sx={{ padding: '0 5px' }}>
                                    <InputLabel>Minimum Length (Chars) </InputLabel>
                                    <OutlinedInput
                                        sx={{ width: '100%' }}
                                        id="passwordSettings"
                                        value={securitySettingsData.passwordSettings.pwdLength}
                                        name="pwdLength"
                                        onChange={handleChangePasswordSettingsData}
                                    />
                                </Grid>
                                <Grid item xs={4} sx={{ padding: '0 5px' }}>
                                    <InputLabel>Missed Attempts Limit (Trys) </InputLabel>
                                    <OutlinedInput
                                        sx={{ width: '100%' }}
                                        id="passwordSettings"
                                        value={securitySettingsData.passwordSettings.attemptsLimit}
                                        name="attemptsLimit"
                                        onChange={handleChangePasswordSettingsData}
                                    />
                                </Grid>
                                <Grid item xs={4} sx={{ padding: '0 5px' }}>
                                    <InputLabel>Expiration Limit (Days) </InputLabel>
                                    <OutlinedInput
                                        sx={{ width: '100%' }}
                                        id="passwordSettings"
                                        value={securitySettingsData.passwordSettings.expirationLimit}
                                        name="expirationLimit"
                                        onChange={handleChangePasswordSettingsData}
                                    />
                                </Grid>
                                <Grid item xs={4} sx={{ padding: '0 5px', mt: 2 }}>
                                    <InputLabel>Reuse Cycle (Times) </InputLabel>
                                    <OutlinedInput
                                        sx={{ width: '100%' }}
                                        id="passwordSettings"
                                        value={securitySettingsData.passwordSettings.reuseCycle}
                                        name="reuseCycle"
                                        onChange={handleChangePasswordSettingsData}
                                    />
                                </Grid>
                                <Grid item xs={4} sx={{ padding: '0 5px', mt: 2 }}>
                                    <InputLabel>Require Capital Letter</InputLabel>
                                    <Switch
                                        color="success"
                                        id="passwordSettings"
                                        checked={securitySettingsData.passwordSettings.requireCapitalLetter}
                                        name="requireCapitalLetter"
                                        onChange={handleChangeSwitchData}
                                    />
                                </Grid>
                                <Grid item xs={4} sx={{ padding: '0 5px', mt: 2 }}>
                                    <InputLabel>Require Lowercase</InputLabel>
                                    <Switch
                                        color="success"
                                        id="passwordSettings"
                                        checked={securitySettingsData.passwordSettings.requireLowercase}
                                        name="requireLowercase"
                                        onChange={handleChangeSwitchData}
                                    />
                                </Grid>
                                <Grid item xs={4} sx={{ padding: '0 5px', mt: 2 }}>
                                    <InputLabel>Require Number</InputLabel>
                                    <Switch
                                        color="success"
                                        id="passwordSettings"
                                        checked={securitySettingsData.passwordSettings.requireNum}
                                        name="requireNum"
                                        onChange={handleChangeSwitchData}
                                    />
                                </Grid>
                                <Grid item xs={4} sx={{ padding: '0 5px', mt: 2 }}>
                                    <InputLabel>Require Special Character</InputLabel>
                                    <Switch
                                        color="success"
                                        id="passwordSettings"
                                        checked={securitySettingsData.passwordSettings.requireSpecialCharacter}
                                        name="requireSpecialCharacter"
                                        onChange={handleChangeSwitchData}
                                    />
                                </Grid>
                                {securitySettingsData.passwordSettings.requireSpecialCharacter && (
                                    <Grid item xs={4} sx={{ padding: '0 5px', mt: 2 }}>
                                        <InputLabel>Special Characters</InputLabel>
                                        <OutlinedInput
                                            sx={{ width: '100%' }}
                                            id="passwordSettings"
                                            value={securitySettingsData.passwordSettings.specialCharacters}
                                            name="specialCharacters"
                                            onChange={handleChangePasswordSettingsData}
                                        />
                                    </Grid>
                                )}
                            </Grid>
                        </SubCard>
                        {securitySettingsData.userInfo.root_admin && (
                            <SubCard title="SERVER Single Sign-On Settings">
                                <Grid container spacing={gridSpacing}>
                                    <Grid item xs={6} sx={{ padding: '0 5px', mt: 2 }}>
                                        <InputLabel>Enable Single-Sign-On</InputLabel>
                                        <Switch
                                            color="success"
                                            id="passwordSettings"
                                            checked={securitySettingsData.passwordSettings.sso}
                                            name="sso"
                                            onChange={handleChangeSwitchData}
                                        />
                                    </Grid>
                                    <Grid item xs={6} sx={{ padding: '0 5px', mt: 2 }}>
                                        <InputLabel>FQDN (Domain)</InputLabel>
                                        <OutlinedInput
                                            sx={{ width: '100%' }}
                                            id="domain"
                                            value={securitySettingsData.domain}
                                            name="domain"
                                            onChange={handleChangePasswordSettingsData}
                                        />
                                    </Grid>
                                </Grid>
                            </SubCard>
                        )}
                    </Grid>
                    <Grid item xs={6}>
                        <SubCard title="Device Upgrade Settings">
                            <Grid container>
                                <Grid item xs={4} sx={{ mt: 2 }}>
                                    <Typography>WakeSMART Client Upgrader</Typography>
                                </Grid>
                                <Grid item xs={8} sx={{ mt: 2 }}>
                                    <Grid container>
                                        <Grid item xs={12} sx={{ display: 'flex', textAlign: 'center', mb: 2 }}>
                                            <Typography sx={{ mt: 1 }}>Enable Auto-Upgrade</Typography>
                                            <Switch
                                                color="success"
                                                name="autoUpgrade"
                                                checked={securitySettingsData.autoUpgrade}
                                                onChange={(e) => {
                                                    setSecuritySettingsData({
                                                        ...securitySettingsData,
                                                        autoUpgrade: e.target.checked
                                                    });
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={3} sx={{ textAlign: 'center' }}>
                                            <Typography>Windows</Typography>
                                            {securitySettingsData.onprem && (
                                                <>
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        sx={{ mt: 0.5 }}
                                                        onClick={() =>
                                                            windowsInstallerInput.current && windowsInstallerInput.current.click()
                                                        }
                                                    >
                                                        Upload Installer
                                                    </Button>
                                                    <input
                                                        ref={windowsInstallerInput}
                                                        type="file"
                                                        accept=".exe,.msi"
                                                        multiple
                                                        style={{ display: 'none' }}
                                                        onChange={(e) => {
                                                            handleInstallerUpload('Windows', e.target.files);
                                                            e.target.value = '';
                                                        }}
                                                    />
                                                </>
                                            )}
                                        </Grid>
                                        <Grid item xs={3} sx={{ textAlign: 'center' }}>
                                            <Typography>MacOS</Typography>
                                            {securitySettingsData.onprem && (
                                                <>
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        sx={{ mt: 0.5 }}
                                                        onClick={() => macInstallerInput.current && macInstallerInput.current.click()}
                                                    >
                                                        Upload Installer
                                                    </Button>
                                                    <input
                                                        ref={macInstallerInput}
                                                        type="file"
                                                        accept=".pkg"
                                                        style={{ display: 'none' }}
                                                        onChange={(e) => {
                                                            handleInstallerUpload('MacOS', e.target.files);
                                                            e.target.value = '';
                                                        }}
                                                    />
                                                </>
                                            )}
                                        </Grid>
                                    </Grid>
                                    <Divider />
                                </Grid>
                                <Grid item xs={4} sx={{ mt: 3 }}>
                                    <Typography>Current Release Versions:</Typography>
                                </Grid>
                                <Grid item xs={8} sx={{ mt: 3 }}>
                                    <Grid container sx={{ textAlign: 'center' }}>
                                        <Grid item xs={3}>
                                            <Typography>
                                                {securitySettingsData.current_versions.windows === -1
                                                    ? 'No Installers'
                                                    : securitySettingsData.current_versions.windows}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={3}>
                                            <Typography>
                                                {securitySettingsData.current_versions.macOS === -1
                                                    ? 'No Installers'
                                                    : securitySettingsData.current_versions.macOS}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </Grid>
                                <Grid item xs={4} sx={{ mt: 3 }}>
                                    <Typography>Auto-Upgrade Version:</Typography>
                                </Grid>
                                <Grid item xs={8} sx={{ mt: 3 }}>
                                    <Grid container sx={{ textAlign: 'center' }}>
                                        <Grid item xs={3}>
                                            {securitySettingsData.available_versions.windows.length === 0 ? (
                                                <Typography>No Upgraders</Typography>
                                            ) : (
                                                <FormControl sx={{ width: '90%' }}>
                                                    <Select
                                                        value={securitySettingsData.selected_versions.windows}
                                                        displayEmpty
                                                        inputProps={{ 'aria-label': 'Without label' }}
                                                        name="windows"
                                                        onChange={handleChangeSelectedVersion}
                                                    >
                                                        {securitySettingsData.available_versions.windows.map((data, key) => (
                                                            <MenuItem value={data} key={key}>
                                                                {data}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            )}
                                        </Grid>
                                        <Grid item xs={3}>
                                            {securitySettingsData.available_versions.macOS.length === 0 ? (
                                                <Typography>No Upgraders</Typography>
                                            ) : (
                                                <FormControl sx={{ width: '90%' }}>
                                                    <Select
                                                        value={securitySettingsData.selected_versions.macOS}
                                                        displayEmpty
                                                        inputProps={{ 'aria-label': 'Without label' }}
                                                        name="macOS"
                                                        onChange={handleChangeSelectedVersion}
                                                    >
                                                        {securitySettingsData.available_versions.macOS.map((data, key) => (
                                                            <MenuItem value={data} key={key}>
                                                                {data}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            )}
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </SubCard>
                        <SubCard title="Sign-on Options">
                            <Grid container>
                                <Grid item xs={6} sx={{ padding: '0 5px', mt: 2 }}>
                                    <InputLabel>Active 2-Factor Authentication</InputLabel>
                                    <Switch
                                        color="success"
                                        id="passwordSettings"
                                        checked={securitySettingsData.passwordSettings.active2Factor}
                                        name="active2Factor"
                                        onChange={handleChangeSwitchData}
                                    />
                                </Grid>
                                <Grid item xs={6} sx={{ padding: '0 5px', mt: 2 }}>
                                    <InputLabel>Enable Wake Portal</InputLabel>
                                    <Switch
                                        color="success"
                                        id="passwordSettings"
                                        checked={securitySettingsData.passwordSettings.wakeportal}
                                        name="wakeportal"
                                        onChange={handleChangeSwitchData}
                                    />
                                </Grid>
                            </Grid>
                        </SubCard>
                    </Grid>
                    {securitySettingsData.passwordSettings.sso && (
                        <Grid item xs={12}>
                            <SubCard title="Single Sign-On Identity Provider">
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    Provide the SAML 2.0 settings from your organization&apos;s identity provider. These values come from
                                    your IdP administrator&apos;s configuration of the WakeSMART application. Use{' '}
                                    <strong>Test Connection</strong> to verify the configuration before users sign in.
                                </Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={6}>
                                        <InputLabel>Identity Provider Type</InputLabel>
                                        <FormControl sx={{ width: '100%' }}>
                                            <Select
                                                displayEmpty
                                                name="idpType"
                                                value={(securitySettingsData.ssoSettings || {}).idpType || ''}
                                                onChange={handleChangeSsoSettings}
                                            >
                                                <MenuItem value="">
                                                    <em>Select a provider…</em>
                                                </MenuItem>
                                                <MenuItem value="entra">Microsoft Entra (Azure AD)</MenuItem>
                                                <MenuItem value="okta">Okta</MenuItem>
                                                <MenuItem value="generic">Generic SAML</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <InputLabel>Application (Client) ID</InputLabel>
                                        <OutlinedInput
                                            sx={{ width: '100%' }}
                                            name="clientId"
                                            value={(securitySettingsData.ssoSettings || {}).clientId || ''}
                                            placeholder="00000000-0000-0000-0000-000000000000"
                                            onChange={handleChangeSsoSettings}
                                        />
                                        <Typography variant="caption" color="text.secondary">
                                            Entra: the Enterprise Application&apos;s Application (client) ID GUID. Required for
                                            per-application certificate validation.
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <InputLabel>Identity Provider URL (Federation Metadata or SSO URL)</InputLabel>
                                        <OutlinedInput
                                            sx={{ width: '100%' }}
                                            name="idpUrl"
                                            value={(securitySettingsData.ssoSettings || {}).idpUrl || ''}
                                            placeholder="https://login.microsoftonline.com/{tenant-id}/federationmetadata/2007-06/federationmetadata.xml"
                                            onChange={handleChangeSsoSettings}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <InputLabel>Identity Provider Entity ID / Issuer</InputLabel>
                                        <OutlinedInput
                                            sx={{ width: '100%' }}
                                            name="idpEntityId"
                                            value={(securitySettingsData.ssoSettings || {}).idpEntityId || ''}
                                            placeholder="https://sts.windows.net/{tenant-id}/"
                                            onChange={handleChangeSsoSettings}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <InputLabel>Identity Provider Signing Certificate (PEM)</InputLabel>
                                        <TextField
                                            sx={{ width: '100%' }}
                                            multiline
                                            minRows={4}
                                            maxRows={10}
                                            name="idpCert"
                                            value={(securitySettingsData.ssoSettings || {}).idpCert || ''}
                                            placeholder={'-----BEGIN CERTIFICATE-----\n...\n-----END CERTIFICATE-----'}
                                            onChange={handleChangeSsoSettings}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Button
                                            color="secondary"
                                            variant="outlined"
                                            disabled={ssoTest.running}
                                            onClick={handleTestSsoConnection}
                                        >
                                            {ssoTest.running ? 'Testing…' : 'Test Connection'}
                                        </Button>
                                    </Grid>
                                    {ssoTest.result && (
                                        <Grid item xs={12}>
                                            <Box
                                                sx={{
                                                    border: '1px solid',
                                                    borderColor: ssoTest.result.ok ? 'success.main' : 'error.main',
                                                    borderRadius: 1,
                                                    p: 2
                                                }}
                                            >
                                                <Typography
                                                    variant="subtitle1"
                                                    sx={{ mb: 1, color: ssoTest.result.ok ? 'success.main' : 'error.main' }}
                                                >
                                                    {ssoTest.result.ok
                                                        ? 'Configuration passed all required checks'
                                                        : 'Configuration has problems that must be fixed'}
                                                </Typography>
                                                {(ssoTest.result.checks || []).map((check, key) => (
                                                    <Box key={key} sx={{ display: 'flex', mb: 0.5 }}>
                                                        <Typography
                                                            sx={{
                                                                minWidth: 70,
                                                                fontWeight: 600,
                                                                color:
                                                                    check.status === 'pass'
                                                                        ? 'success.main'
                                                                        : check.status === 'warn'
                                                                          ? 'warning.main'
                                                                          : 'error.main'
                                                            }}
                                                        >
                                                            {check.status === 'pass' ? 'PASS' : check.status === 'warn' ? 'WARN' : 'FAIL'}
                                                        </Typography>
                                                        <Typography variant="body2">
                                                            <strong>{check.name}:</strong> {check.detail}
                                                        </Typography>
                                                    </Box>
                                                ))}
                                            </Box>
                                        </Grid>
                                    )}
                                </Grid>
                            </SubCard>
                        </Grid>
                    )}
                    {recaptcha.canEdit && (
                        <Grid item xs={12}>
                            <SubCard title="reCAPTCHA">
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    Google reCAPTCHA v2 site key used on the password and user dialogs. This is a public value that applies
                                    to the whole server. Editable by Root Admins only.
                                </Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <InputLabel>reCAPTCHA Site Key</InputLabel>
                                        <OutlinedInput
                                            sx={{ width: '100%' }}
                                            name="recaptchaSiteKey"
                                            value={recaptcha.siteKey}
                                            placeholder="6Lc..."
                                            onChange={(e) => setRecaptcha((prev) => ({ ...prev, siteKey: e.target.value }))}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Button color="secondary" variant="contained" onClick={handleSaveRecaptchaKey}>
                                            Save reCAPTCHA Key
                                        </Button>
                                    </Grid>
                                </Grid>
                            </SubCard>
                        </Grid>
                    )}
                </Grid>
            </MainCard>
        );
    }
};

export default SecuritySettings;
